import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabaseHarvest } from '@/lib/supabase';
import {
  JOB_DEFAULTS,
  kindFromJobId,
  labelFromJobId,
  providerFromJobId,
  type JobDefault,
} from '@/config/jobs';
import type { AdminAction, CronOverride } from '@/types/harvest';
import {
  HOST_API_CONFIGURED,
  evaluateSchedule,
  fetchCatalog,
  fetchScheduleVersions,
  optimizeSchedule,
  restoreScheduleVersion,
  type ScheduleScope,
} from '@/lib/hostApi';

export interface JobRow extends JobDefault {
  override: CronOverride | null;
  effectiveCron: string;
  enabled: boolean;
}

export function useCronOverrides() {
  return useQuery({
    queryKey: ['cron-overrides'],
    queryFn: async (): Promise<CronOverride[]> => {
      const { data, error } = await supabaseHarvest
        .from('cron_schedule_overrides')
        .select('*');
      if (error) throw error;
      return (data ?? []) as CronOverride[];
    },
  });
}

/**
 * The job roster for the Cron CRUD page, joined against cron_schedule_overrides.
 *
 * The authoritative roster of WHICH jobs exist comes from the Host /catalog
 * endpoint (the real JobRegistry, ~70 jobs across the real Hangfire queues),
 * enriched with curated presentation metadata from JOB_DEFAULTS. When the Host
 * API is unavailable we fall back to JOB_DEFAULTS alone (the legacy behavior).
 */
export function useEffectiveJobs() {
  const overrides = useCronOverrides();
  const catalog = useCronCatalog();

  // A catalog fetch failure is non-fatal — we fall back to JOB_DEFAULTS — but it
  // means the screen may be missing registry jobs, so surface it in the console.
  if (catalog.isError) {
    console.warn('Cron catalog fetch failed; falling back to JOB_DEFAULTS (some jobs may be missing).', catalog.error);
  }

  // useMemo keeps the rows reference stable across renders when the underlying
  // data hasn't changed, so consumers that depend on `jobs` don't refire.
  const rows = useMemo<JobRow[]>(() => {
    const overrideFor = (jobId: string) => overrides.data?.find((o) => o.job_id === jobId) ?? null;

    if (catalog.data && catalog.data.length > 0) {
      return catalog.data.map((c) => {
        const meta = JOB_DEFAULTS.find((d) => d.jobId === c.jobId);
        const override = overrideFor(c.jobId);
        return {
          jobId: c.jobId,
          label: meta?.label ?? labelFromJobId(c.jobId),
          provider: meta?.provider ?? providerFromJobId(c.jobId),
          queue: c.queue,
          kind: meta?.kind ?? kindFromJobId(c.jobId),
          level: meta?.level ?? 'L2',
          defaultCron: c.defaultCron,
          description: meta?.description,
          override,
          effectiveCron: c.effectiveCron,
          enabled: c.enabled,
        } satisfies JobRow;
      });
    }

    return JOB_DEFAULTS.map((d) => {
      const override = overrideFor(d.jobId);
      return {
        ...d,
        override,
        effectiveCron: override?.cron_expression ?? d.defaultCron,
        enabled: override ? override.enabled : true,
      } satisfies JobRow;
    });
  }, [catalog.data, overrides.data]);

  return {
    ...overrides,
    jobs: rows,
    isLoading: overrides.isLoading || catalog.isLoading,
  };
}

export function useUpsertOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      job_id: string;
      cron_expression: string;
      queue?: string | null;
      enabled: boolean;
      reason?: string | null;
      // FK to auth.users — populates harvest.cron_schedule_overrides.updated_by
      // so the audit trail records which admin made the change.
      updated_by?: string | null;
    }) => {
      const { error } = await supabaseHarvest.from('cron_schedule_overrides').upsert(
        {
          job_id: payload.job_id,
          cron_expression: payload.cron_expression,
          queue: payload.queue ?? null,
          enabled: payload.enabled,
          reason: payload.reason ?? null,
          updated_by: payload.updated_by ?? null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'job_id' },
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cron-overrides'] }),
  });
}

export function useDeleteOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabaseHarvest
        .from('cron_schedule_overrides')
        .delete()
        .eq('job_id', jobId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cron-overrides'] }),
  });
}

/** Queues an admin action (trigger_job, pause_job, etc.) for the .NET host to process. */
export function useQueueAdminAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<AdminAction, 'action_id' | 'requested_at' | 'processed_at' | 'hangfire_job_id' | 'error_message' | 'status'>) => {
      const { data, error } = await supabaseHarvest
        .from('admin_actions')
        .insert({
          action_type: payload.action_type,
          job_id: payload.job_id,
          payload: payload.payload,
          requested_by: payload.requested_by,
        })
        .select()
        .single();
      if (error) throw error;
      return data as AdminAction;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-actions'] }),
  });
}

export function useRecentAdminActions(limit = 25) {
  return useQuery({
    queryKey: ['admin-actions', limit],
    queryFn: async (): Promise<AdminAction[]> => {
      const { data, error } = await supabaseHarvest
        .from('admin_actions')
        .select('*')
        .order('requested_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as AdminAction[];
    },
    refetchInterval: 10_000,
  });
}

// --- Schedule optimizer + evaluation + versioning (issue #148) ---
// These go through the .NET Host (catalog/evaluate/optimize/versions/restore),
// so they are gated on VITE_HOST_API_URL being configured.

/** Authoritative job catalog from the Host registry (overlaid with overrides). */
export function useCronCatalog() {
  return useQuery({
    queryKey: ['cron-catalog'],
    queryFn: fetchCatalog,
    enabled: HOST_API_CONFIGURED,
  });
}

/** Server-authoritative schedule health report. */
export function useScheduleEvaluation() {
  return useQuery({
    queryKey: ['schedule-evaluation'],
    queryFn: evaluateSchedule,
    enabled: HOST_API_CONFIGURED,
  });
}

/** Schedule version history (txid groups), newest first. */
export function useScheduleVersions(limit = 50) {
  return useQuery({
    queryKey: ['schedule-versions', limit],
    queryFn: () => fetchScheduleVersions(limit),
    enabled: HOST_API_CONFIGURED,
  });
}

/** Runs the optimizer for a provider scope; returns a proposal (does not persist). */
export function useOptimizeSchedule() {
  return useMutation({
    mutationFn: (scope: ScheduleScope) => optimizeSchedule(scope),
  });
}

/** Restores a prior schedule version through the Host commit path. */
export function useRestoreVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (txid: number) => restoreScheduleVersion(txid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cron-overrides'] });
      qc.invalidateQueries({ queryKey: ['cron-catalog'] });
      qc.invalidateQueries({ queryKey: ['schedule-versions'] });
      qc.invalidateQueries({ queryKey: ['schedule-evaluation'] });
    },
  });
}
